/**
 * Dataset **row-filter** editor (ADR-0028 N3 "filtered Dataset").
 *
 * A catalog Dataset can carry an always-AND row filter on `config.filter`
 * (`{match, conditions:[{field, op, value}]}`) so a scoped surface sees only a
 * slice of a shared dataset. The filter is **unbypassable** at query time and
 * invisible to the assistant; here the operator authors it as a small
 * query-builder (the shared `ConditionRows`, also used by the metric editor).
 * Saved via `PATCH /datasets/{id}` (`filter`), validated server-side by
 * `validate_filter_spec` — a bad filter is rejected here.
 *
 * Operator console (datasets catalog) — native selects are acceptable.
 */
import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ConditionRows,
  rowsIncomplete,
  rowsToSpec,
  specToRows,
  type ConditionRow,
} from "@/components/datasets/conditionBuilder";
import {
  datasetsApi,
  type Dataset,
  type DatasetFilterSpec,
} from "@/lib/api-datasets";

export function DatasetFilterDialog({
  dataset,
  open,
  onClose,
}: {
  dataset: Dataset;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const existing = (dataset.config?.filter ?? undefined) as DatasetFilterSpec | undefined;
  const [match, setMatch] = React.useState<"all" | "any">(existing?.match ?? "all");
  const [rows, setRows] = React.useState<ConditionRow[]>(() => specToRows(existing));

  const fieldsQ = useQuery({
    queryKey: ["datasets", dataset.id, "fields"],
    queryFn: () => datasetsApi.fields(dataset.id),
    enabled: open,
  });
  const fields = fieldsQ.data ?? [];

  // Fields load async — backfill any row whose field is still empty (e.g. a row
  // added before the field list arrived) with the first available field.
  React.useEffect(() => {
    if (!fieldsQ.data?.length) return;
    const first = fieldsQ.data[0].name;
    setRows((r) => (r.some((row) => !row.field) ? r.map((row) => (row.field ? row : { ...row, field: first })) : r));
  }, [fieldsQ.data]);

  const save = useMutation({
    // No rows ⇒ clear the filter (rowsToSpec returns null).
    mutationFn: () => datasetsApi.update(dataset.id, { filter: rowsToSpec(match, rows) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["datasets"] });
      onClose();
    },
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t("datasets.filterTitle", "Row filter")} — {dataset.name}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <p className="text-xs text-muted-foreground">
            {t(
              "datasets.filterHelp",
              "Always-applied row filter for this dataset. Every read (assistant or BI) only sees rows that match — it can't be widened or bypassed.",
            )}
          </p>

          <ConditionRows
            fields={fields}
            rows={rows}
            match={match}
            onRowsChange={setRows}
            onMatchChange={setMatch}
            emptyHint={t(
              "datasets.filterNone",
              "No filter — the whole dataset is visible. Add a condition to restrict it.",
            )}
          />

          {save.isError && (
            <Alert tone="danger" title={t("datasets.filterSaveFailed", "Could not save filter")}>
              <span className="font-mono text-xs">{(save.error as Error).message}</span>
            </Alert>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button disabled={save.isPending || rowsIncomplete(rows)} onClick={() => save.mutate()}>
            {rows.length === 0
              ? t("datasets.filterClear", "Clear filter")
              : t("common.save", "Save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
